import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as yaml from 'js-yaml';
import './PipelineEditor.css';

// Detect pipeline type from content
const detectPipelineType = (content, filename = '') => {
  if (filename.includes('azure-pipelines') || filename.includes('.azure')) {
    return 'azure';
  }
  if (filename.includes('.gitlab-ci')) {
    return 'gitlab';
  }
  // Check content for Azure DevOps specific keywords
  if (content.includes('trigger:') || content.includes('pr:') || content.includes('pool:')) {
    return 'azure';
  }
  // Default to GitLab
  return 'gitlab';
};

// Azure DevOps Parser - converts to GitLab-like structure for compatibility
const parseAzurePipeline = (content) => {
  try {
    const parsed = yaml.load(content);
    
    const config = {
      includes: [],
      defaultImage: parsed.pool?.vmImage || 'ubuntu-latest',
      stages: [],
      jobs: [],
      globalVariables: parsed.variables || {},
      cache: null,
      beforeScript: [],
      afterScript: [],
      _azureMetadata: {
        trigger: parsed.trigger || null,
        pool: parsed.pool || null,
        resources: parsed.resources || null
      }
    };

    // Parse stages - convert Azure stages to GitLab-like format
    if (parsed.stages && Array.isArray(parsed.stages)) {
      config.stages = parsed.stages.map(stage => {
        if (typeof stage === 'string') {
          return stage;
        }
        // For Azure stage objects, store them with their structure
        return {
          name: stage.stage || stage.displayName || 'unnamed',
          displayName: stage.displayName || stage.stage || 'Unnamed Stage',
          _azureJobs: stage.jobs || []
        };
      });
      
      // Extract jobs from stages
      parsed.stages.forEach(stage => {
        if (stage.jobs && Array.isArray(stage.jobs)) {
          stage.jobs.forEach(job => {
            const stageName = stage.stage || stage.displayName || 'unnamed';
            config.jobs.push({
              name: job.job || job.displayName || 'unnamed-job',
              displayName: job.displayName || job.job || 'Unnamed Job',
              stage: stageName,
              isTemplate: false,
              image: job.pool?.vmImage || null,
              script: job.steps ? job.steps.map(step => {
                if (typeof step === 'string') return step;
                if (step.script) return step.script;
                if (step.task) return `task: ${step.task}`;
                return JSON.stringify(step);
              }) : [],
              beforeScript: [],
              afterScript: [],
              variables: job.variables || {},
              cache: null,
              artifacts: null,
              rules: [],
              needs: [],
              services: [],
              tags: [],
              when: 'on_success',
              allowFailure: false
            });
          });
        }
      });
    }

    // Parse jobs (if no stages defined)
    if (parsed.jobs && Array.isArray(parsed.jobs)) {
      if (config.stages.length === 0) {
        config.stages = ['default'];
      }
      parsed.jobs.forEach(job => {
        config.jobs.push({
          name: job.job || job.displayName || 'unnamed-job',
          displayName: job.displayName || job.job || 'Unnamed Job',
          stage: config.stages[0],
          isTemplate: false,
          image: job.pool?.vmImage || null,
          script: job.steps ? job.steps.map(step => {
            if (typeof step === 'string') return step;
            if (step.script) return step.script;
            if (step.task) return `task: ${step.task}`;
            return JSON.stringify(step);
          }) : [],
          beforeScript: [],
          afterScript: [],
          variables: job.variables || {},
          cache: null,
          artifacts: null,
          rules: [],
          needs: [],
          services: [],
          tags: [],
          when: 'on_success',
          allowFailure: false
        });
      });
    }

    return config;
  } catch (error) {
    console.error('Error parsing Azure Pipeline:', error);
    return null;
  }
};

// GitLab CI Parser
const parseGitLabPipeline = (content) => {
  try {
    const parsed = yaml.load(content);
    
    const config = {
      includes: [],
      defaultImage: 'node:18',
      stages: [],
      jobs: [],
      globalVariables: {},
      cache: null,
      beforeScript: [],
      afterScript: []
    };

    // Parse includes
    if (parsed.include) {
      const includes = Array.isArray(parsed.include) ? parsed.include : [parsed.include];
      config.includes = includes.map(inc => ({
        file: inc.file || '',
        project: inc.project || '',
        ref: inc.ref || 'master'
      }));
    }

    // Parse default settings
    if (parsed.default) {
      if (parsed.default.image) {
        config.defaultImage = parsed.default.image;
      }
      if (parsed.default.before_script) {
        config.beforeScript = parsed.default.before_script;
      }
      if (parsed.default.after_script) {
        config.afterScript = parsed.default.after_script;
      }
      if (parsed.default.cache) {
        config.cache = parsed.default.cache;
      }
    }

    // Parse global variables
    if (parsed.variables) {
      config.globalVariables = parsed.variables;
    }

    // Parse stages
    if (parsed.stages) {
      config.stages = parsed.stages;
    }

    // Parse jobs
    Object.keys(parsed).forEach(key => {
      // Skip reserved keywords
      if (['include', 'default', 'variables', 'stages', 'workflow', 'cache'].includes(key)) {
        return;
      }

      const job = parsed[key];
      if (typeof job === 'object' && job !== null) {
        const jobConfig = {
          name: key,
          isTemplate: key.startsWith('.'),
          stage: job.stage || '',
          image: job.image || null,
          script: job.script || [],
          beforeScript: job.before_script || [],
          afterScript: job.after_script || [],
          variables: job.variables || {},
          cache: job.cache || null,
          artifacts: job.artifacts || null,
          rules: job.rules || [],
          needs: job.needs || [],
          services: job.services || [],
          tags: job.tags || [],
          when: job.when || 'on_success',
          allowFailure: job.allow_failure || false,
          retry: job.retry || null,
          timeout: job.timeout || null,
          extends: job.extends || null
        };

        config.jobs.push(jobConfig);
      }
    });

    return config;
  } catch (error) {
    console.error('Error parsing GitLab CI:', error);
    return null;
  }
};

// Generate GitLab CI YAML from config
const generateGitLabPipeline = (config) => {
  const output = {};

  // Add includes
  if (config.includes && config.includes.length > 0) {
    output.include = config.includes.map(inc => ({
      file: inc.file,
      project: inc.project,
      ref: inc.ref
    }));
  }

  // Add default settings
  const defaultConfig = {};
  if (config.defaultImage) {
    defaultConfig.image = config.defaultImage;
  }
  if (config.beforeScript && config.beforeScript.length > 0) {
    defaultConfig.before_script = config.beforeScript;
  }
  if (config.afterScript && config.afterScript.length > 0) {
    defaultConfig.after_script = config.afterScript;
  }
  if (config.cache) {
    defaultConfig.cache = config.cache;
  }
  if (Object.keys(defaultConfig).length > 0) {
    output.default = defaultConfig;
  }

  // Add stages
  if (config.stages && config.stages.length > 0) {
    output.stages = config.stages;
  }

  // Add global variables
  if (config.globalVariables && Object.keys(config.globalVariables).length > 0) {
    output.variables = config.globalVariables;
  }

  // Add jobs
  config.jobs.forEach(job => {
    const jobConfig = {};
    
    if (job.stage) jobConfig.stage = job.stage;
    if (job.image) jobConfig.image = job.image;
    if (job.script && job.script.length > 0) jobConfig.script = job.script;
    if (job.beforeScript && job.beforeScript.length > 0) jobConfig.before_script = job.beforeScript;
    if (job.afterScript && job.afterScript.length > 0) jobConfig.after_script = job.afterScript;
    if (job.variables && Object.keys(job.variables).length > 0) jobConfig.variables = job.variables;
    if (job.cache) jobConfig.cache = job.cache;
    if (job.artifacts) jobConfig.artifacts = job.artifacts;
    if (job.rules && job.rules.length > 0) jobConfig.rules = job.rules;
    if (job.needs && job.needs.length > 0) jobConfig.needs = job.needs;
    if (job.services && job.services.length > 0) jobConfig.services = job.services;
    if (job.tags && job.tags.length > 0) jobConfig.tags = job.tags;
    if (job.when && job.when !== 'on_success') jobConfig.when = job.when;
    if (job.allowFailure) jobConfig.allow_failure = job.allowFailure;
    if (job.retry) jobConfig.retry = job.retry;
    if (job.timeout) jobConfig.timeout = job.timeout;
    if (job.extends) jobConfig.extends = job.extends;

    output[job.name] = jobConfig;
  });

  return yaml.dump(output, { lineWidth: -1, noRefs: true });
};

// Generate Azure DevOps YAML from config
const generateAzurePipeline = (config) => {
  const output = {};

  // Restore Azure-specific metadata
  if (config._azureMetadata) {
    if (config._azureMetadata.trigger) output.trigger = config._azureMetadata.trigger;
    if (config._azureMetadata.pool) output.pool = config._azureMetadata.pool;
    if (config._azureMetadata.resources) output.resources = config._azureMetadata.resources;
  }
  
  if (config.globalVariables && Object.keys(config.globalVariables).length > 0) {
    output.variables = config.globalVariables;
  }

  if (config.stages && config.stages.length > 0) {
    output.stages = config.stages.map(stage => {
      const stageName = typeof stage === 'string' ? stage : (stage.name || 'unnamed');
      const stageDisplay = typeof stage === 'string' ? stage : (stage.displayName || stage.name || 'Unnamed');
      
      // Get jobs for this stage
      const stageJobs = config.jobs.filter(job => job.stage === stageName);
      
      return {
        stage: stageName,
        displayName: stageDisplay,
        jobs: stageJobs.map(job => ({
          job: job.name,
          displayName: job.displayName || job.name,
          pool: job.image ? { vmImage: job.image } : undefined,
          steps: job.script.map(script => ({ script })),
          variables: Object.keys(job.variables || {}).length > 0 ? job.variables : undefined
        })).filter(job => job !== null)
      };
    });
  }

  return yaml.dump(output, { lineWidth: -1, noRefs: true });
};

const PipelineEditor = ({ onInsert, currentContent = '', filename = '' }) => {
  const [pipelineType, setPipelineType] = useState('gitlab');
  const [gitlabConfig, setGitlabConfig] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [activeTab, setActiveTab] = useState('pipeline'); // pipeline, overview, includes, jobs, variables
  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef(null);
  const lastSavedConfigRef = useRef(null);

  // Parse existing content when component mounts or content changes
  useEffect(() => {
    if (currentContent && currentContent.trim().length > 0) {
      setIsEditMode(true);
      
      // Detect pipeline type
      const detectedType = detectPipelineType(currentContent, filename);
      setPipelineType(detectedType);
      
      if (detectedType === 'azure') {
        const parsed = parseAzurePipeline(currentContent);
        if (parsed) {
          setGitlabConfig(parsed);
        }
      } else {
        const parsed = parseGitLabPipeline(currentContent);
        if (parsed) {
          setGitlabConfig(parsed);
        }
      }
    } else {
      // Initialize with default config based on filename
      const detectedType = detectPipelineType('', filename);
      setPipelineType(detectedType);
      
      if (detectedType === 'azure') {
        setGitlabConfig({
          includes: [],
          defaultImage: 'ubuntu-latest',
          stages: [
            {
              name: 'Build',
              displayName: 'Build Stage',
              _azureJobs: []
            }
          ],
          jobs: [],
          globalVariables: {},
          cache: null,
          beforeScript: [],
          afterScript: [],
          _azureMetadata: {
            trigger: ['main'],
            pool: { vmImage: 'ubuntu-latest' },
            resources: null
          }
        });
      } else {
        setGitlabConfig({
          includes: [],
          defaultImage: 'node:18',
          stages: ['build', 'test', 'deploy'],
          jobs: [
            {
              name: 'build-job',
              isTemplate: false,
              stage: 'build',
              image: null,
              script: ['npm install', 'npm run build'],
              beforeScript: [],
              afterScript: [],
              variables: {},
              cache: null,
              artifacts: null,
              rules: [],
              needs: [],
              services: [],
              tags: [],
              when: 'on_success',
              allowFailure: false
            }
          ],
          globalVariables: {},
          cache: null,
          beforeScript: [],
          afterScript: []
        });
      }
    }
  }, [currentContent]);

  // Auto-save with debounce
  useEffect(() => {
    if (!gitlabConfig) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Check if config has changed
    const currentConfigStr = JSON.stringify(gitlabConfig);
    if (lastSavedConfigRef.current === currentConfigStr) {
      setIsSaving(false);
      return; // No changes, skip save
    }

    // Show saving indicator
    setIsSaving(true);

    // Set debounced save (1.5 seconds)
    saveTimeoutRef.current = setTimeout(() => {
      const generatedYaml = pipelineType === 'azure' 
        ? generateAzurePipeline(gitlabConfig)
        : generateGitLabPipeline(gitlabConfig);
      
      const defaultFilename = pipelineType === 'azure' ? 'azure-pipelines.yml' : '.gitlab-ci.yml';
      
      onInsert({
        filename: filename || defaultFilename,
        content: generatedYaml,
        platform: pipelineType
      });
      lastSavedConfigRef.current = currentConfigStr;
      setIsSaving(false);
    }, 1500);

    // Cleanup on unmount or before next effect
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [gitlabConfig, filename, onInsert, pipelineType]);

  // Save on unmount (when closing editor)
  useEffect(() => {
    return () => {
      if (gitlabConfig) {
        const currentConfigStr = JSON.stringify(gitlabConfig);
        if (lastSavedConfigRef.current !== currentConfigStr) {
          const generatedYaml = pipelineType === 'azure' 
            ? generateAzurePipeline(gitlabConfig)
            : generateGitLabPipeline(gitlabConfig);
          
          const defaultFilename = pipelineType === 'azure' ? 'azure-pipelines.yml' : '.gitlab-ci.yml';
          
          onInsert({
            filename: filename || defaultFilename,
            content: generatedYaml,
            platform: pipelineType
          });
        }
      }
    };
  }, []);

  const addInclude = () => {
    setGitlabConfig({
      ...gitlabConfig,
      includes: [...gitlabConfig.includes, { file: '', project: '', ref: 'master' }]
    });
  };

  const updateInclude = (index, field, value) => {
    const newIncludes = [...gitlabConfig.includes];
    newIncludes[index][field] = value;
    setGitlabConfig({ ...gitlabConfig, includes: newIncludes });
  };

  const removeInclude = (index) => {
    const newIncludes = gitlabConfig.includes.filter((_, i) => i !== index);
    setGitlabConfig({ ...gitlabConfig, includes: newIncludes });
  };

  const addStage = () => {
    const stageName = prompt('Enter stage name:');
    if (stageName && !gitlabConfig.stages.includes(stageName)) {
      setGitlabConfig({
        ...gitlabConfig,
        stages: [...gitlabConfig.stages, stageName]
      });
    }
  };

  const removeStage = (stageName) => {
    if (confirm(`Remove stage "${stageName}"? Jobs in this stage will need to be reassigned.`)) {
      setGitlabConfig({
        ...gitlabConfig,
        stages: gitlabConfig.stages.filter(s => s !== stageName)
      });
    }
  };

  const addJob = () => {
    const newJob = {
      name: `new-job-${gitlabConfig.jobs.length + 1}`,
      isTemplate: false,
      stage: gitlabConfig.stages[0] || 'build',
      image: null,
      script: [],
      beforeScript: [],
      afterScript: [],
      variables: {},
      cache: null,
      artifacts: null,
      rules: [],
      needs: [],
      services: [],
      tags: [],
      when: 'on_success',
      allowFailure: false
    };
    setGitlabConfig({
      ...gitlabConfig,
      jobs: [...gitlabConfig.jobs, newJob]
    });
    setSelectedJob(gitlabConfig.jobs.length);
  };

  const updateJob = (index, updates) => {
    const newJobs = [...gitlabConfig.jobs];
    newJobs[index] = { ...newJobs[index], ...updates };
    setGitlabConfig({ ...gitlabConfig, jobs: newJobs });
  };

  const removeJob = (index) => {
    const newJobs = gitlabConfig.jobs.filter((_, i) => i !== index);
    setGitlabConfig({ ...gitlabConfig, jobs: newJobs });
    if (selectedJob === index) setSelectedJob(null);
  };

  const addGlobalVariable = () => {
    const key = prompt('Enter variable name:');
    if (key) {
      setGitlabConfig({
        ...gitlabConfig,
        globalVariables: { ...gitlabConfig.globalVariables, [key]: '' }
      });
    }
  };

  const updateGlobalVariable = (key, value) => {
    setGitlabConfig({
      ...gitlabConfig,
      globalVariables: { ...gitlabConfig.globalVariables, [key]: value }
    });
  };

  const removeGlobalVariable = (key) => {
    const newVars = { ...gitlabConfig.globalVariables };
    delete newVars[key];
    setGitlabConfig({ ...gitlabConfig, globalVariables: newVars });
  };

  if (!gitlabConfig) {
    return <div className="pipeline-editor-panel">Loading...</div>;
  }

  const pipelineName = pipelineType === 'azure' ? 'Azure DevOps Pipeline' : 'GitLab CI Pipeline';

  return (
    <div className="pipeline-editor-panel">
      <div className="pipeline-editor-header">
        <h3>🔧 {pipelineName} {isEditMode ? 'Editor' : 'Generator'}</h3>
        <p>{isEditMode ? `Edit your ${pipelineName} configuration` : `Create ${pipelineName} for your project`}</p>
        {isEditMode && (
          <div className="edit-mode-badge">
            ✏️ Editing existing pipeline - {gitlabConfig.jobs.length} jobs, {gitlabConfig.stages.length} stages
          </div>
        )}
        <div className={`auto-save-indicator ${!isSaving ? 'saved' : ''}`}>
          {isSaving ? (
            <>
              <span>💾</span>
              <span>Auto-saving...</span>
            </>
          ) : (
            <>
              <span>✅</span>
              <span>All changes saved</span>
            </>
          )}
        </div>
      </div>

      <div className="pipeline-editor-tabs">
        <button 
          className={`tab-btn ${activeTab === 'pipeline' ? 'active' : ''}`}
          onClick={() => setActiveTab('pipeline')}
        >
          🔄 Pipeline
        </button>
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button 
          className={`tab-btn ${activeTab === 'includes' ? 'active' : ''}`}
          onClick={() => setActiveTab('includes')}
        >
          📦 Includes ({gitlabConfig.includes.length})
        </button>
        <button 
          className={`tab-btn ${activeTab === 'jobs' ? 'active' : ''}`}
          onClick={() => setActiveTab('jobs')}
        >
          ⚙️ Jobs ({gitlabConfig.jobs.length})
        </button>
        <button 
          className={`tab-btn ${activeTab === 'variables' ? 'active' : ''}`}
          onClick={() => setActiveTab('variables')}
        >
          🔧 Variables ({Object.keys(gitlabConfig.globalVariables).length})
        </button>
      </div>

      <div className="pipeline-editor-content">
        {activeTab === 'pipeline' && (
          <div className="tab-content pipeline-visual">
            <div className="pipeline-canvas">
              <div className="pipeline-stages-container">
                {gitlabConfig.stages.map((stage, stageIdx) => {
                  // Handle both string stages (GitLab) and object stages (Azure)
                  const stageName = typeof stage === 'string' ? stage : (stage.name || stage.displayName || 'Unnamed');
                  const stageDisplay = typeof stage === 'string' ? stage : (stage.displayName || stage.name || 'Unnamed');
                  const stageJobs = gitlabConfig.jobs.filter(job => job.stage === stageName);
                  return (
                    <div key={stageIdx} className="pipeline-stage-column">
                      <div className="stage-header">
                        <h4>{stageDisplay}</h4>
                        <button 
                          className="stage-remove-btn"
                          onClick={() => removeStage(stageName)}
                          title="Remove stage"
                        >
                          ×
                        </button>
                      </div>
                      
                      <div className="stage-jobs-list">
                        {stageJobs.map((job, jobIdx) => {
                          const jobRealIdx = gitlabConfig.jobs.indexOf(job);
                          return (
                            <div 
                              key={jobRealIdx} 
                              className={`job-card ${selectedJob === jobRealIdx ? 'selected' : ''} ${job.isTemplate ? 'template' : ''}`}
                              onClick={() => setSelectedJob(jobRealIdx)}
                            >
                              <div className="job-card-header">
                                <span className="job-icon">{job.isTemplate ? '📋' : '⚙️'}</span>
                                <span className="job-name">{job.name}</span>
                              </div>
                              <div className="job-card-details">
                                <span className="job-stage-badge">{job.stage}</span>
                                {job.script && job.script.length > 0 && (
                                  <span className="job-script-count">{job.script.length} steps</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        
                        <button 
                          className="add-job-btn"
                          onClick={() => {
                            const newJob = {
                              name: `${stageName}-job-${stageJobs.length + 1}`,
                              isTemplate: false,
                              stage: stageName,
                              image: null,
                              script: [],
                              beforeScript: [],
                              afterScript: [],
                              variables: {},
                              cache: null,
                              artifacts: null,
                              rules: [],
                              needs: [],
                              services: [],
                              tags: [],
                              when: 'on_success',
                              allowFailure: false
                            };
                            setGitlabConfig({
                              ...gitlabConfig,
                              jobs: [...gitlabConfig.jobs, newJob]
                            });
                            setSelectedJob(gitlabConfig.jobs.length);
                          }}
                        >
                          + Add Job
                        </button>
                      </div>
                      
                      {stageIdx < gitlabConfig.stages.length - 1 && (
                        <div className="stage-connector">→</div>
                      )}
                    </div>
                  );
                })}
                
                <div className="pipeline-stage-column add-stage-column">
                  <button className="add-stage-btn" onClick={addStage}>
                    <span className="add-icon">+</span>
                    <span>Add Stage</span>
                  </button>
                </div>
              </div>
            </div>

            {selectedJob !== null && (
              <div className="job-config-panel">
                <div className="config-panel-header">
                  <h4>Configure Job: {gitlabConfig.jobs[selectedJob]?.name}</h4>
                  <button 
                    className="close-config-btn"
                    onClick={() => setSelectedJob(null)}
                  >
                    ×
                  </button>
                </div>
                
                <div className="config-panel-content">
                  <div className="form-group">
                    <label>Job Name:</label>
                    <input
                      type="text"
                      value={gitlabConfig.jobs[selectedJob]?.name || ''}
                      onChange={(e) => updateJob(selectedJob, { name: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Stage:</label>
                    <select
                      value={gitlabConfig.jobs[selectedJob]?.stage || ''}
                      onChange={(e) => updateJob(selectedJob, { stage: e.target.value })}
                    >
                      {gitlabConfig.stages.map((stage, idx) => {
                        const stageName = typeof stage === 'string' ? stage : (stage.name || stage.displayName || 'Unnamed');
                        const stageDisplay = typeof stage === 'string' ? stage : (stage.displayName || stage.name || 'Unnamed');
                        return (
                          <option key={idx} value={stageName}>{stageDisplay}</option>
                        );
                      })}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Image (optional):</label>
                    <input
                      type="text"
                      value={gitlabConfig.jobs[selectedJob]?.image || ''}
                      onChange={(e) => updateJob(selectedJob, { image: e.target.value || null })}
                      placeholder="node:18 (leave empty to use default)"
                    />
                  </div>

                  <div className="form-group">
                    <label>Script Steps:</label>
                    {gitlabConfig.jobs[selectedJob]?.script?.map((cmd, idx) => (
                      <div key={idx} className="script-item">
                        <input
                          type="text"
                          value={cmd}
                          onChange={(e) => {
                            const newScript = [...gitlabConfig.jobs[selectedJob].script];
                            newScript[idx] = e.target.value;
                            updateJob(selectedJob, { script: newScript });
                          }}
                        />
                        <button
                          className="remove-btn-small"
                          onClick={() => {
                            const newScript = gitlabConfig.jobs[selectedJob].script.filter((_, i) => i !== idx);
                            updateJob(selectedJob, { script: newScript });
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <button
                      className="add-btn-small"
                      onClick={() => {
                        const newScript = [...(gitlabConfig.jobs[selectedJob]?.script || []), ''];
                        updateJob(selectedJob, { script: newScript });
                      }}
                    >
                      + Add Step
                    </button>
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={gitlabConfig.jobs[selectedJob]?.isTemplate || false}
                        onChange={(e) => updateJob(selectedJob, { 
                          isTemplate: e.target.checked,
                          name: e.target.checked && !gitlabConfig.jobs[selectedJob].name.startsWith('.') 
                            ? `.${gitlabConfig.jobs[selectedJob].name}` 
                            : gitlabConfig.jobs[selectedJob].name.replace(/^\./, '')
                        })}
                      />
                      Template job (reusable)
                    </label>
                  </div>

                  <button 
                    className="remove-btn"
                    onClick={() => {
                      if (confirm(`Delete job "${gitlabConfig.jobs[selectedJob]?.name}"?`)) {
                        removeJob(selectedJob);
                      }
                    }}
                  >
                    🗑️ Delete Job
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'overview' && (
          <div className="tab-content">
            <div className="form-section">
              <h4>Default Settings</h4>
              <div className="form-group">
                <label>Default Image:</label>
                <input
                  type="text"
                  value={gitlabConfig.defaultImage}
                  onChange={(e) => setGitlabConfig({ ...gitlabConfig, defaultImage: e.target.value })}
                  placeholder="node:18"
                />
              </div>
            </div>

            <div className="form-section">
              <h4>Stages</h4>
              <div className="stages-list">
                {gitlabConfig.stages.map((stage, idx) => {
                  const stageName = typeof stage === 'string' ? stage : (stage.name || stage.displayName || 'Unnamed');
                  const stageDisplay = typeof stage === 'string' ? stage : (stage.displayName || stage.name || 'Unnamed');
                  return (
                    <div key={idx} className="stage-item">
                      <span className="stage-number">{idx + 1}</span>
                      <span className="stage-name">{stageDisplay}</span>
                      <button 
                        className="remove-btn-small"
                        onClick={() => removeStage(stageName)}
                        title="Remove stage"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
              <button className="add-btn-small" onClick={addStage}>
                + Add Stage
              </button>
            </div>
          </div>
        )}

        {activeTab === 'includes' && (
          <div className="tab-content">
            <div className="form-section">
              <h4>External Includes</h4>
              <p className="help-text">Include external YAML files from other projects</p>
              {gitlabConfig.includes.map((inc, idx) => (
                <div key={idx} className="include-item">
                  <div className="form-group">
                    <label>File Path:</label>
                    <input
                      type="text"
                      value={inc.file}
                      onChange={(e) => updateInclude(idx, 'file', e.target.value)}
                      placeholder="/common/base-build.yml"
                    />
                  </div>
                  <div className="form-group">
                    <label>Project:</label>
                    <input
                      type="text"
                      value={inc.project}
                      onChange={(e) => updateInclude(idx, 'project', e.target.value)}
                      placeholder="platform/pipelines-templates"
                    />
                  </div>
                  <div className="form-group">
                    <label>Ref/Branch:</label>
                    <input
                      type="text"
                      value={inc.ref}
                      onChange={(e) => updateInclude(idx, 'ref', e.target.value)}
                      placeholder="master"
                    />
                  </div>
                  <button 
                    className="remove-btn"
                    onClick={() => removeInclude(idx)}
                  >
                    Remove Include
                  </button>
                </div>
              ))}
              <button className="add-btn" onClick={addInclude}>
                + Add Include
              </button>
            </div>
          </div>
        )}

        {activeTab === 'jobs' && (
          <div className="tab-content jobs-tab">
            <div className="jobs-sidebar">
              <div className="jobs-list">
                {gitlabConfig.jobs.map((job, idx) => (
                  <div
                    key={idx}
                    className={`job-item ${selectedJob === idx ? 'active' : ''} ${job.isTemplate ? 'template' : ''}`}
                    onClick={() => setSelectedJob(idx)}
                  >
                    <span className="job-name">{job.isTemplate ? '.' : ''}{job.name}</span>
                    <span className="job-stage">{job.stage}</span>
                  </div>
                ))}
              </div>
              <button className="add-btn" onClick={addJob}>
                + Add Job
              </button>
            </div>

            <div className="job-details">
              {selectedJob !== null && gitlabConfig.jobs[selectedJob] ? (
                <div className="job-editor">
                  <h4>Edit Job: {gitlabConfig.jobs[selectedJob].name}</h4>
                  
                  <div className="form-group">
                    <label>Job Name:</label>
                    <input
                      type="text"
                      value={gitlabConfig.jobs[selectedJob].name}
                      onChange={(e) => updateJob(selectedJob, { name: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Stage:</label>
                    <select
                      value={gitlabConfig.jobs[selectedJob].stage}
                      onChange={(e) => updateJob(selectedJob, { stage: e.target.value })}
                    >
                      {gitlabConfig.stages.map((stage, idx) => {
                        const stageName = typeof stage === 'string' ? stage : (stage.name || stage.displayName || 'Unnamed');
                        const stageDisplay = typeof stage === 'string' ? stage : (stage.displayName || stage.name || 'Unnamed');
                        return (
                          <option key={idx} value={stageName}>{stageDisplay}</option>
                        );
                      })}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Image (optional):</label>
                    <input
                      type="text"
                      value={gitlabConfig.jobs[selectedJob].image || ''}
                      onChange={(e) => updateJob(selectedJob, { image: e.target.value || null })}
                      placeholder="Use default image"
                    />
                  </div>

                  <div className="form-group">
                    <label>Script:</label>
                    <textarea
                      value={gitlabConfig.jobs[selectedJob].script.join('\n')}
                      onChange={(e) => updateJob(selectedJob, { script: e.target.value.split('\n').filter(l => l.trim()) })}
                      rows={5}
                      placeholder="npm install&#10;npm run build"
                    />
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={gitlabConfig.jobs[selectedJob].isTemplate}
                        onChange={(e) => updateJob(selectedJob, { 
                          isTemplate: e.target.checked,
                          name: e.target.checked && !gitlabConfig.jobs[selectedJob].name.startsWith('.') 
                            ? `.${gitlabConfig.jobs[selectedJob].name}` 
                            : gitlabConfig.jobs[selectedJob].name.replace(/^\./, '')
                        })}
                      />
                      Template job (hidden job starting with .)
                    </label>
                  </div>

                  <button 
                    className="remove-btn"
                    onClick={() => removeJob(selectedJob)}
                  >
                    🗑️ Delete Job
                  </button>
                </div>
              ) : (
                <div className="no-selection">
                  <p>Select a job to edit or create a new one</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'variables' && (
          <div className="tab-content">
            <div className="form-section">
              <h4>Global Variables</h4>
              <p className="help-text">Variables available to all jobs</p>
              {Object.entries(gitlabConfig.globalVariables).map(([key, value]) => (
                <div key={key} className="variable-item">
                  <div className="form-group">
                    <label>{key}:</label>
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => updateGlobalVariable(key, e.target.value)}
                    />
                  </div>
                  <button 
                    className="remove-btn-small"
                    onClick={() => removeGlobalVariable(key)}
                    title="Remove variable"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button className="add-btn" onClick={addGlobalVariable}>
                + Add Variable
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PipelineEditor;
